import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Stepper, Step, StepLabel, Button, Box, Typography, Paper, Alert, Snackbar, CircularProgress, } from "@mui/material";
import { ArrowBack, ArrowForward, CheckCircle } from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { pdfjs } from "react-pdf";
import mammoth from "mammoth";
import { useAuth } from '../../context/AuthContext';
import HRPersonalDetails from './components/HRPersonalDetails';
import HREducationDetails from './components/HREducationDetails';
import HRExperienceDetails from './components/HRExperienceDetails';
import HRDocumentsPreferences from './components/HRDocumentsPreferences';
import HRReviewSubmit from './components/HRReviewSubmit';
import { getCurrentFYear } from '../../utils/financialYear';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const steps = ["Personal Details", "Education Details", "Experience Details", "Documents & Preference", "Review & Submit"];

const deepMerge = (target, source) => {
    const output = { ...target };
    for (const key in source) {
        if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
            output[key] = deepMerge(target[key] || {}, source[key]);
        } else {
            output[key] = source[key];
        }
    }
    return output;
};

export default function CandidateMaster({ mode = "add", onPopupCancel, connectionString, companyData }) {
    const { user, selectedCompany, loading } = useAuth();
    const { id } = useParams();
    const navigate = useNavigate();
    const path = `${connectionString}/candidate`;
    console.log(selectedCompany)
    const companyId = selectedCompany?.CompId || null;
    const gCompId = selectedCompany?.GCompId || null;  // Use same access pattern
    const fYear = getCurrentFYear();
    const userId = user?.UserId || null;

    const [activeStep, setActiveStep] = useState(0);
    const [saving, setSaving] = useState(false);
    const location = useLocation();
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [visibleEduCount, setVisibleEduCount] = useState(1);
    const [visibleExpCount, setVisibleExpCount] = useState(1)
    const [allowBackNavigation, setAllowBackNavigation] = useState(false);
    const [formData, setFormData] = useState({
        name: "", mobileNo: "", emailId: "", dob: "", gender: "Male", address: "", nativePlace: "", applyFor: "", applyForOther: "", source: "",
        referenceBy: "",
        educationDetails: [
            { id: null, qualification: "", collegeName: "", passingYear: "", percentage: "" }  // ✅ ADDED id: null
        ],
        hasExperience: "Yes",
        experienceYears: "", experienceMonths: "",
        experienceDetails: [
            { id: null, companyName: "", jobTitle: "", salaryPerMonth: "", reasonForLeaving: "", fromDate: "", toDate: "", currentlyWorking: false }
        ],
        englishProficiency: "", languagesKnown: "", salaryExpected: "", joiningDate: "", noticePeriod: "", pinCode: "", dateOfApplication: "", altMobileNo: "", maritalStatus: "", spouseName: "", spouseContact: "", bloodGroup: "", fatherName: "",
        fatherContact: "", motherName: "", motherContact: ""
    });

    const [city, setCity] = useState("");
    const [stateField, setStateField] = useState("");
    const [cities, setCities] = useState([]);
    const [country, setCountry] = useState("");

    const [errors, setErrors] = useState({});
    // Document states
    const [documents, setDocuments] = useState([]); // Already uploaded from DB
    const [pendingFiles, setPendingFiles] = useState({ Resume: null, Photo: null, IdProof: null }); // New files to upload
    const [previews, setPreviews] = useState({ Resume: null, Photo: null, IdProof: null }); // For preview

    //  For Fallback ------------------------------------------------------------------------------------
    const [isDirty, setIsDirty] = useState(false);

    // Track initial form state on load (for Edit mode comparison)
    const [initialFormState, setInitialFormState] = useState(null);

    // Handle change of fields
    const handleEducationChange = (index, field, value) => {
        const updated = [...formData.educationDetails];
        updated[index][field] = value;

        setFormData(prev => ({
            ...prev,
            educationDetails: updated
        }));
    };

    // Add new blank education entry
    const addEducation = () => {
        setFormData(prev => ({
            ...prev,
            educationDetails: [
                ...prev.educationDetails,
                { id: null, qualification: "", collegeName: "", passingYear: "", percentage: "" }  // ✅ ADDED id: null
            ]
        }));

        setVisibleEduCount(prev => prev + 1);
    };

    // Remove education entry
    const removeEducation = (index) => {
        const updated = formData.educationDetails.filter((_, i) => i !== index);

        setFormData(prev => ({
            ...prev,
            educationDetails: updated
        }));

        // decrease visible count
        setVisibleEduCount(prev => Math.max(prev - 1, 1));
    };

    const handleExpChange = (index, field, value) => {
        setFormData(prev => {
            const updated = [...prev.experienceDetails];
            updated[index][field] = value;

            return { ...prev, experienceDetails: updated };
        });
    };

    const addExperience = () => {
        setFormData(prev => ({
            ...prev,
            experienceDetails: [
                ...prev.experienceDetails,
                {
                    id: null,
                    companyName: "",
                    jobTitle: "",
                    salaryPerMonth: "",
                    reasonForLeaving: "",
                    fromDate: "",
                    toDate: "",
                    currentlyWorking: false
                }
            ]
        }));
        setVisibleExpCount(prev => prev + 1);
    };

    // Remove education entry
    const removeExperience = (index) => {
        const updated = formData.experienceDetails.filter((_, i) => i !== index);
        setFormData(prev => ({
            ...prev,
            experienceDetails: updated
        }));

        // decrease visible count
        setVisibleExpCount(prev => Math.max(prev - 1, 1));
    };

    const handleCurrentlyWorking = (index, checked) => {
        setFormData(prev => {
            const updated = prev.experienceDetails.map((exp, i) => {
                if (i === index) {
                    // If checked → clear toDate & set currentlyWorking true
                    return {
                        ...exp,
                        currentlyWorking: checked,
                        toDate: checked ? "" : exp.toDate
                    };
                }

                // If checked → all other experience entries should be false
                return {
                    ...exp,
                    currentlyWorking: checked ? false : exp.currentlyWorking
                };
            });

            return { ...prev, experienceDetails: updated };
        });
    };

    useEffect(() => {
        if (location.state?.snackbar) {
            setSnackbar({
                open: true,
                message: location.state.snackbar.message,
                severity: location.state.snackbar.severity
            });

            // clear history state (prevents re-show on refresh)
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);

    // Track if user has typed/uploaded anything (for Add mode)
    useEffect(() => {
        if (mode === "add") {
            setIsDirty(false);
            return;
        }

        // In Edit mode: Save initial state when data loads
        if (documents.length > 0 || formData.name) {
            setInitialFormState({
                formData,
                documents,
                pendingFiles
            });
        }
    }, [mode, documents.length]); // Run once when docs load

    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (false) {
                e.preventDefault();
                e.returnValue = ''; // Chrome requires this
                return '';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [formData, pendingFiles, documents, mode, initialFormState]);

    useEffect(() => {
        const handlePopState = (e) => {
            if (false) {
                if (window.confirm("You have unsaved changes. Leave anyway?")) {
                    navigate(1); // Allow leave
                } else {
                    e.preventDefault();
                    window.history.pushState(null, '', window.location.href); // Stay
                }
            }
        };

        window.addEventListener('popstate', handlePopState);

        return () => window.removeEventListener('popstate', handlePopState);
    }, [formData, pendingFiles, documents, mode, initialFormState]);

    const hasUnsavedChanges = () => {
        if (mode === "edit") {
            if (!initialFormState) return false;

            const formChanged = JSON.stringify(formData) !== JSON.stringify(initialFormState.formData);
            const filesChanged = pendingFiles.Resume || pendingFiles.Photo;

            return formChanged || filesChanged;
        }

        // === ADD MODE: Only consider user-entered data ===
        const userFilledFields = [
            formData.name,
            formData.mobileNo,
            formData.emailId,
            formData.dob,
            formData.address,
            formData.nativePlace,
            formData.applyFor,
            formData.qualification,
            formData.collegeName,
            formData.passingYear,
            formData.percentage,
            formData.companyName,
            formData.jobTitle,
            formData.salaryPerMonth,
            formData.reasonForLeaving,
            formData.englishProficiency,
            formData.languagesKnown
        ];

        const hasUserInput = userFilledFields.some(val => val && val.toString().trim() !== "");
        const hasFiles = pendingFiles.Resume || pendingFiles.Photo;

        return hasUserInput || hasFiles;
    };
    //  For Fallback ------------------------------------------------------------------------------------

    // Load candidate on edit
    useEffect(() => {
        if (mode === "edit" && id) {
            axios.get(`${path}/getDetails/${id}`).then(res => {
                const d = res.data.data;

                // Set city, state, country
                setCity(d.City || "");
                setStateField(d.State || "");
                setCountry(d.Country || "");

                // Set form data
                setFormData(prev => ({
                    ...prev,
                    name: d.CandidateName || "",
                    mobileNo: d.MobileNo || "",
                    emailId: d.EmailId || "",
                    dob: d.DateOfBirth ? dayjs(d.DateOfBirth).format("YYYY-MM-DD") : "",
                    gender: d.Gender || "Male",
                    address: d.Address || "",
                    nativePlace: d.NativePlace || "",
                    applyFor: d.AppliedFor || "",
                    applyForOther: d.ApplyForOther || "",

                    // Education Details Array - INCLUDE ID FOR UPDATE
                    educationDetails: d.educationDetails && d.educationDetails.length > 0
                        ? d.educationDetails.map(edu => ({
                            id: edu.id || edu.CandidateEducationId || null,  // ✅ INCLUDE ID
                            qualification: edu.qualification || edu.Qualification || "",
                            collegeName: edu.collegeName || edu.CollegeName || "",
                            passingYear: edu.passingYear || edu.PassingYear || "",
                            percentage: edu.percentage || edu.Percentage || ""
                        }))
                        : [{ id: null, qualification: "", collegeName: "", passingYear: "", percentage: "" }],

                    // Experience
                    hasExperience: (d.ExperienceYears > 0 || d.ExperienceMonths > 0 || (d.experienceDetails && d.experienceDetails.length > 0)) ? "Yes" : "No",
                    experienceYears: d.ExperienceYears != null ? String(Math.floor(d.ExperienceYears)) : "",
                    experienceMonths: d.ExperienceMonths != null ? String(Math.trunc(Number(d.ExperienceMonths))) : "",

                    // Experience Details Array - INCLUDE ID FOR UPDATE
                    experienceDetails: d.experienceDetails && d.experienceDetails.length > 0
                        ? d.experienceDetails.map(exp => ({
                            id: exp.id || exp.CandidateExperianceId || null,  // ✅ INCLUDE ID
                            companyName: exp.companyName || exp.CompanyName || "",
                            jobTitle: exp.jobTitle || exp.JobTitle || "",
                            salaryPerMonth: exp.salaryPerMonth || exp.SalaryPerMonth || "",
                            reasonForLeaving: exp.reasonForLeaving || exp.ReasonForLeaving || "",
                            fromDate: exp.fromDate || exp.FromDate ? dayjs(exp.fromDate || exp.FromDate).format("YYYY-MM-DD") : "",
                            toDate: exp.toDate || exp.ToDate ? dayjs(exp.toDate || exp.ToDate).format("YYYY-MM-DD") : "",
                            currentlyWorking: exp.currentlyWorking || exp.CurrentlyWorking || false
                        }))
                        : [{ id: null, companyName: "", jobTitle: "", salaryPerMonth: "", reasonForLeaving: "", fromDate: "", toDate: "", currentlyWorking: false }],

                    // Preferences
                    englishProficiency: d.EnglishProficiency || "",
                    languagesKnown: d.LanguagesKnown || "",
                    salaryExpected: d.salaryExpected || d.ExpectedCTC || "",
                    joiningDate: d.joiningDate || d.JoiningDate ? dayjs(d.joiningDate || d.JoiningDate).format("YYYY-MM-DD") : "",
                    noticePeriod: d.noticePeriod || d.NoticePeriod || "",

                    // Personal
                    pinCode: d.pinCode || d.Pincode || "",
                    dateOfApplication: d.dateOfApplication || d.DateOfApplication ? dayjs(d.dateOfApplication || d.DateOfApplication).format("YYYY-MM-DD") : "",
                    altMobileNo: d.altMobileNo || d.AlternateMobile || "",
                    maritalStatus: d.maritalStatus || d.MaritalStatus || "",
                    spouseName: d.spouseName || d.SpouseName || "",
                    spouseContact: d.spouseContact || d.SpouseContact || "",
                    bloodGroup: d.bloodGroup || d.BloodGroup || "",
                    fatherName: d.fatherName || d.FatherName || "",
                    fatherContact: d.fatherContact || d.FatherContact || "",
                    motherName: d.motherName || d.MotherName || "",
                    motherContact: d.motherContact || d.MotherContact || "",
                    source: d.Source || "",
                    referenceBy: d.ReferenceBy || ""
                }));

                // Set visible counts based on loaded data
                if (d.educationDetails && d.educationDetails.length > 0) {
                    setVisibleEduCount(d.educationDetails.length);
                }
                if (d.experienceDetails && d.experienceDetails.length > 0) {
                    setVisibleExpCount(d.experienceDetails.length);
                }

                console.log("===========================================");
                console.log("=== FORM DATA SET ===");
                console.log("===========================================");

                // Load documents
                axios.get(`${path}/documents/${id}`).then(docRes => {
                    console.log("Documents:", docRes.data.documents);
                    setDocuments(docRes.data.documents || []);
                });
            }).catch((err) => {
                console.error("❌ Failed to load candidate:", err);
                setSnackbar({ open: true, message: "Failed to load candidate", severity: "error" });
            });
        }
    }, [mode, id, path]);

    const handleChange = (field) => (e) => {
        setFormData({ ...formData, [field]: e.target.value });
    };

    const handleFileSelect = async (file, type) => {
        if (!file) return;
        if (file.size > 20 * 1024 * 1024) {
            setSnackbar({ open: true, message: "File must be < 20MB", severity: "error" });
            return;
        }

        setPendingFiles(prev => ({ ...prev, [type]: file }));

        const reader = new FileReader();
        const ext = file.name.split(".").pop().toLowerCase();

        if (type === "Photo" || file.type.startsWith("image/")) {
            reader.onload = () => setPreviews(prev => ({ ...prev, [type]: { url: reader.result, type: "image" } }));
            reader.readAsDataURL(file);
        } else if (ext === "pdf") {
            reader.onload = () => setPreviews(prev => ({ ...prev, [type]: { url: reader.result, type: "pdf" } }));
            reader.readAsDataURL(file);
        } else if (ext === "doc" || ext === "docx") {
            reader.onload = async () => {
                const { value } = await mammoth.convertToHtml({ arrayBuffer: reader.result });
                setPreviews(prev => ({ ...prev, [type]: { html: value, type: "doc" } }));
            };
            reader.readAsArrayBuffer(file);
        }
    };

    const removeFile = (type) => {
        setPendingFiles(prev => ({ ...prev, [type]: null }));
        setPreviews(prev => ({ ...prev, [type]: null }));
    };

    console.log("formDataaaaaa", formData)

    const handleNext = () => {
        const required = getRequiredFields(activeStep);

        if (!validateCurrentStep(activeStep)) {
            const newErrors = {};

            required.forEach((field) => {

                // 1 File validations (same)
                if (field === "Resume" || field === "Photo") {
                    if (!pendingFiles[field] && !documents.find(d => d.DocumentType === field)) {
                        newErrors[field] = true;
                    }
                    return;
                }

                // 2 Dynamic Education Fields
                if (field.startsWith("educationDetails")) {
                    const match = field.match(/educationDetails\[(\d+)\]\.(\w+)/);
                    if (match) {
                        const index = Number(match[1]);
                        const key = match[2];

                        if (
                            !formData.educationDetails[index][key] ||
                            formData.educationDetails[index][key].toString().trim() === ""
                        ) {
                            // Example key: "educationDetails_0_percentage"
                            newErrors[`educationDetails_${index}_${key}`] = true;
                        }
                    }
                    return;
                }
                // 3 Dynamic Experience Fields
                if (field.startsWith("experienceDetails")) {
                    const match = field.match(/experienceDetails\[(\d+)\]\.(\w+)/);
                    if (match) {
                        const index = Number(match[1]);
                        const key = match[2];

                        if (
                            !formData.experienceDetails[index][key] ||
                            formData.experienceDetails[index][key].toString().trim() === ""
                        ) {
                            newErrors[`experienceDetails_${index}_${key}`] = true;
                        }
                    }
                    return;
                }

                // 4 Normal fields
                if (!formData[field] || formData[field] === "") {
                    newErrors[field] = true;
                }
            });

            setErrors(newErrors);
            alert("Please fill all required fields before proceeding.");
            return;
        }

        // If valid → clear errors and go next
        setErrors({});

        if (activeStep === steps.length - 1) {
            handleFinalSubmit();
        } else {
            setActiveStep((prev) => prev + 1);
        }
    };

    const handleBack = () => {
        setAllowBackNavigation(true);

        if (activeStep > 0) {
            setActiveStep(prev => prev - 1);   // ✅ move stepper back
        }
    };


    const getRequiredFields = (step) => {
        switch (step) {
            case 0: // Personal
                const personalRequired = [
                    "name", "mobileNo", "emailId", "dob", "gender", "address", "applyFor",
                    "pinCode", "dateOfApplication", "maritalStatus", "source"
                ];

                // Add spouse fields *only if married*
                // if (formData.maritalStatus === "Married") {
                //     personalRequired.push("spouseName", "spouseContact");
                // }

                if (formData.applyFor === "Other") {
                    personalRequired.push("applyForOther");
                }

                return personalRequired;
            case 1: // Education
                return formData.educationDetails.flatMap((edu, index) =>
                    ["qualification", "collegeName", "passingYear", "percentage"]
                        .filter(field => !edu[field] || edu[field].toString().trim() === "")
                        .map(field => `educationDetails[${index}].${field}`)
                );
            case 2: // Experience
                const base = ["hasExperience"];

                // If user selected NO → only required = ["hasExperience"]
                if (formData.hasExperience !== "Yes") {
                    return base;
                }

                // Otherwise validate dynamic experience entries
                const experienceRequired = formData.experienceDetails.flatMap((exp, index) => {
                    const fields = ["companyName", "jobTitle", "fromDate", "salaryPerMonth", "reasonForLeaving"];

                    // toDate is required only if NOT currently working
                    if (!exp.currentlyWorking) fields.push("toDate");

                    return fields
                        .filter(field => !exp[field] || exp[field].toString().trim() === "")
                        .map(field => `experienceDetails[${index}].${field}`);
                });

                return [...base, ...experienceRequired];

            case 3: // Documents & Preference
                return ["Resume", "Photo"]; // we check file uploads here
            case 4: // Review – no validation needed to go next (submit)
                return [];
            default:
                return [];
        }
    };

    const validateCurrentStep = (step) => {
        const required = getRequiredFields(step);

        const missingField = required.find((field) => {

            // File validation (unchanged)
            if (field === "Resume" || field === "Photo") {
                return !pendingFiles[field] && !documents.find(d => d.DocumentType === field);
            }

            // New: Dynamic Education validation
            if (field.startsWith("educationDetails")) {
                const match = field.match(/educationDetails\[(\d+)\]\.(\w+)/);
                if (match) {
                    const index = Number(match[1]);
                    const key = match[2];
                    return (
                        !formData.educationDetails[index][key] ||
                        formData.educationDetails[index][key].toString().trim() === ""
                    );
                }
            }

            // Normal fields (same as before)
            return !formData[field] || formData[field] === "";
        });

        return !missingField; // true = valid
    };


    const handleFinalSubmit = async () => {
        if (!formData.name || !formData.mobileNo || !formData.applyFor) {
            setSnackbar({
                open: true,
                message: "Name, Mobile & Apply For are required",
                severity: "error"
            });
            return;
        }

        setSaving(true);

        const payload = {
            meta: { id: mode === "edit" ? id : undefined, GCompId: gCompId, CompId: companyId, FYear: fYear, userId: userId },
            personal: { CandidateName: formData.name, MobileNo: formData.mobileNo, EmailId: formData.emailId || null, DOB: formData.dob || null, Gender: formData.gender, Address: formData.address || null, city: city || null, state: stateField || null, country: country || null, NativePlace: formData.nativePlace || null, AppliedFor: formData.applyFor, ApplyForOther: formData.applyFor === "Other" ? formData.applyForOther : null, pinCode: formData.pinCode ? Number(formData.pinCode) : null, dateOfApplication: formData.dateOfApplication || null, altMobileNo: formData.altMobileNo || "", maritalStatus: formData.maritalStatus || "", spouseName: formData.spouseName || "", spouseContact: formData.spouseContact || "", bloodGroup: formData.bloodGroup || "", fatherName: formData.fatherName || "", fatherContact: formData.fatherContact || "", motherName: formData.motherName || "", motherContact: formData.motherContact || "", Source: formData.source, ReferenceBy: formData.referenceBy || null },
            education: formData.educationDetails,
            experience: { ExperienceYears: formData.hasExperience === "Yes" ? parseFloat(formData.experienceYears || 0) : 0, ExperienceMonths: parseFloat(formData.experienceMonths || 0), experienceDetails: formData.experienceDetails },
            preference: {
                salaryExpected: formData.salaryExpected || "", joiningDate: formData.joiningDate || "", noticePeriod: formData.noticePeriod || null, englishProficiency: formData.englishProficiency || "", languagesKnown: formData.languagesKnown || "",
                documents: [
                    // already uploaded documents (from DB)
                    ...documents.map(d => ({
                        DocumentType: d.DocumentType,
                        FileName: d.FileName || "",
                        downloadUrl: d.downloadUrl || null
                    })),

                    // newly selected / pending documents
                    ...Object.keys(pendingFiles)
                        .filter(key => pendingFiles[key])
                        .map(key => ({
                            DocumentType: key,
                            FileName: pendingFiles[key].name
                        }))
                ]
            }

        };

        console.log("FINAL PAYLOAD:", payload);

        try {
            let candidateId = id;

            if (mode === "add") {
                const res = await axios.post(`${path}/insert`, payload);
                candidateId = res.data.candidateId || res.data.id;
            } else {
                await axios.put(`${path}/update/${id}`, payload);
            }

            for (const [docType, file] of Object.entries(pendingFiles)) {
                if (file) {
                    const formDataUpload = new FormData();
                    formDataUpload.append("document", file);
                    // formDataUpload.append("UploadedBy", userId);
                    // formDataUpload.append("CompId", companyId);
                    // formDataUpload.append("fYear", fYear);
                    // formDataUpload.append("candidateId", candidateId);
                    // formDataUpload.append("documentType", docType);
                    formDataUpload.append("UploadedBy", userId);
                    if (gCompId != null) {
                        formDataUpload.append("GCompId", gCompId);
                    }
                    if (fYear != null) {
                        formDataUpload.append("FYear", fYear);
                    }

                    formDataUpload.append("CompId", companyId);
                    formDataUpload.append("candidateId", candidateId);
                    formDataUpload.append("documentType", docType);


                    await axios.post(`${path}/upload-document`, formDataUpload, {
                        headers: { "Content-Type": "multipart/form-data" }
                    });
                }
            }

            sessionStorage.setItem(
                "candidateSnackbar",
                JSON.stringify({
                    message: mode === "add"
                        ? "Your data inserted successfully"
                        : "Your data updated successfully",
                    severity: "success"
                })
            );

            navigate(-1);


        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.message || "Save failed", severity: "error" });
        } finally {
            setSaving(false);
        }
    };

    const getStepContent = (step) => {
        switch (step) {
            case 0:
                return (
                    <HRPersonalDetails formData={formData} setFormData={setFormData} city={city} setCity={setCity} stateField={stateField} setStateField={setStateField} country={country} setCountry={setCountry} cities={cities} errors={errors} handleChange={handleChange} connectionString={connectionString} />
                );
            case 1:
                return (
                    <HREducationDetails formData={formData} errors={errors} visibleEduCount={visibleEduCount} addEducation={addEducation} removeEducation={removeEducation} handleEducationChange={handleEducationChange} />
                );
            case 2:
                return (
                    <HRExperienceDetails formData={formData} setFormData={setFormData} visibleExpCount={visibleExpCount} addExperience={addExperience} removeExperience={removeExperience} handleExpChange={handleExpChange} handleCurrentlyWorking={handleCurrentlyWorking} handleChange={handleChange} />
                );
            case 3:
                return (
                    <HRDocumentsPreferences formData={formData} setFormData={setFormData} documents={documents} pendingFiles={pendingFiles} previews={previews} handleFileSelect={handleFileSelect} removeFile={removeFile} connectionString={connectionString} handleChange={handleChange} />
                );
            case 4:
                return (
                    <HRReviewSubmit formData={formData} documents={documents} pendingFiles={pendingFiles} setActiveStep={setActiveStep} />
                );
            default:
                return null;
        }
    };


    const isStepValid = validateCurrentStep(activeStep);

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
            <Paper elevation={8} sx={{ maxWidth: 1100, mx: "auto", mt: 3, p: 4 }}>
                <Typography variant="h4" align="center" gutterBottom color="primary" fontWeight="bold">
                    {mode === "add" ? "Add New Candidate" : "Edit Candidate"}
                </Typography>

                <Stepper activeStep={activeStep} alternativeLabel sx={{ my: 4 }}>
                    {steps.map(l => <Step key={l}><StepLabel>{l}</StepLabel></Step>)}
                </Stepper>

                <Box sx={{ minHeight: 520 }}>{getStepContent(activeStep)}</Box>

                <Box display="flex" justifyContent="space-between" mt={5}>
                    <Button startIcon={<ArrowBack />} disabled={activeStep === 0} onClick={handleBack} variant="outlined">Back</Button>
                    {activeStep === steps.length - 1 ? (
                        <Button variant="contained" color="success" onClick={handleFinalSubmit} disabled={saving} startIcon={saving ? <CircularProgress size={20} /> : <CheckCircle />}> {saving ? "Saving..." : "Submit"} </Button>
                    ) : (
                        <Button variant="contained" endIcon={<ArrowForward />} onClick={handleNext} disabled={!isStepValid} >Next</Button>
                    )}
                </Box>

                <Snackbar anchorOrigin={{ vertical: "bottom", horizontal: "left" }} open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} >
                    <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: "100%" }} > {snackbar.message} </Alert>
                </Snackbar>
            </Paper>
        </LocalizationProvider>
    );
}